<script setup>
import { computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import ProviderLogStatusBadge from './ProviderLogStatusBadge.vue';
import ProviderLogDirectionBadge from './ProviderLogDirectionBadge.vue';
import ProviderLogAttachmentBadge from './ProviderLogAttachmentBadge.vue';

const props = defineProps({
  logs: {
    type: Array,
    default: () => [],
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const tableHeaders = computed(() => [
  t('MESSAGE_LOGS_PAGE.TABLE.TIME'),
  t('MESSAGE_LOGS_PAGE.TABLE.DIRECTION'),
  t('MESSAGE_LOGS_PAGE.TABLE.STATUS'),
  t('MESSAGE_LOGS_PAGE.TABLE.INBOX'),
  t('MESSAGE_LOGS_PAGE.TABLE.CONTACT'),
  t('MESSAGE_LOGS_PAGE.TABLE.SOURCE_ID'),
  t('MESSAGE_LOGS_PAGE.TABLE.MESSAGE'),
  t('MESSAGE_LOGS_PAGE.TABLE.ERROR'),
]);

const inboxNameFor = inboxId => {
  const inbox = props.inboxes.find(item => item.id === inboxId);
  return inbox ? inbox.name : t('MESSAGE_LOGS_PAGE.NO_INBOX');
};

// `created_at` comes straight from the provider_logs controller's `render
// json:`, which serializes it as an ISO 8601 string (Rails' default for a
// Time value) rather than the `.to_i` unix-timestamp convention the rest of
// the dashboard's timeHelper (messageStamp/dynamicTime/…) assumes. Parsing
// with `new Date` handles that string directly; the numeric branch is kept
// only so this still works if that serialization is ever aligned to `.to_i`.
const formatLogTimestamp = value => {
  if (value === null || value === undefined || value === '') return '—';
  const isNumeric = typeof value === 'number' || /^\d+$/.test(String(value));
  const date = isNumeric ? new Date(Number(value) * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'MMM dd, yyyy hh:mm a');
};
</script>

<template>
  <div class="w-full overflow-x-auto">
    <BaseTable
      :headers="tableHeaders"
      :items="logs"
      :loading="loading"
      :no-data-message="t('MESSAGE_LOGS_PAGE.EMPTY_STATE')"
    >
      <template #header-0="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #header-1="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #header-2="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #header-3="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #header-4="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #header-5="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #header-6="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #header-7="{ header }">
        <span class="normal-case">{{ header }}</span>
      </template>

      <template #row="{ items }">
        <BaseTableRow v-for="log in items" :key="log.id" :item="log">
          <template #default>
            <BaseTableCell class="whitespace-nowrap">
              <span class="text-body-main text-n-slate-11">
                {{ formatLogTimestamp(log.createdAt) }}
              </span>
            </BaseTableCell>

            <BaseTableCell class="whitespace-nowrap">
              <ProviderLogDirectionBadge :direction="log.messageType" />
            </BaseTableCell>

            <BaseTableCell class="whitespace-nowrap">
              <ProviderLogStatusBadge :status="log.status" />
            </BaseTableCell>

            <BaseTableCell class="whitespace-nowrap">
              <span class="text-body-main text-n-slate-12">
                {{ inboxNameFor(log.inboxId) }}
              </span>
            </BaseTableCell>

            <BaseTableCell class="whitespace-nowrap">
              <div
                v-if="log.contactName || log.contactPhoneNumber"
                class="flex flex-col"
              >
                <span class="text-body-main text-n-slate-12">
                  {{ log.contactName || log.contactPhoneNumber }}
                </span>
                <span
                  v-if="log.contactName && log.contactPhoneNumber"
                  class="text-label-small text-n-slate-10"
                >
                  {{ log.contactPhoneNumber }}
                </span>
              </div>
              <span v-else class="text-body-main text-n-slate-10">
                {{ t('MESSAGE_LOGS_PAGE.NO_CONTACT') }}
              </span>
            </BaseTableCell>

            <BaseTableCell class="whitespace-nowrap">
              <span class="text-body-main text-n-slate-11">
                {{ log.sourceId || '—' }}
              </span>
            </BaseTableCell>

            <BaseTableCell class="max-w-64">
              <span
                v-if="log.content"
                class="text-body-main text-n-slate-12 line-clamp-1"
              >
                {{ log.content }}
              </span>
              <ProviderLogAttachmentBadge
                v-else-if="log.attachmentType"
                :attachment-type="log.attachmentType"
              />
              <span v-else class="text-body-main text-n-slate-10">—</span>
            </BaseTableCell>

            <BaseTableCell class="max-w-64">
              <span
                v-if="log.externalError"
                v-tooltip.top="log.externalError"
                class="text-body-main text-n-ruby-11 line-clamp-1"
              >
                {{ log.externalError }}
              </span>
              <span v-else class="text-body-main text-n-slate-10">—</span>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>
  </div>
</template>
